"use client";

import { useState } from "react";
import { PillGroup, SelectField } from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL } from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { ArithmeticSheet } from "@/components/tools/worksheets-math/arithmetic-sheet";
import { WORKSHEET_COMMON_STRINGS } from "@/components/tools/worksheets-math/common-strings";
import {
  CommonControls,
  defaultSettings,
  UnmetNotice,
  useCommonSettings,
  useWorksheetSeed,
} from "@/components/tools/worksheets-math/worksheet-kit";
import {
  type DivisionOptions,
  generateDivision,
  type RemainderMode,
} from "@/lib/tool-kit/worksheets/arithmetic";
import { createRng } from "@/lib/tool-kit/worksheets/random";

/** English strings. A translated content file overrides these through `ui`. */
export const DIVISION_WORKSHEET_GENERATOR_STRINGS = {
  ...WORKSHEET_COMMON_STRINGS,
  defaultTitle: "Division Practice",
  modeLabel: "Practice type",
  modes: { facts: "Division facts", digits: "Long division" },
  presetsLabel: "Quick levels",
  presets: {
    tables: "Facts, divisors 2–5",
    tablesAll: "Facts, divisors 2–12",
    short: "3-digit ÷ 1-digit",
    shortRem: "3-digit ÷ 1-digit with remainders",
    long: "4-digit ÷ 2-digit",
  },
  divisorFromLabel: "Divisor from",
  divisorToLabel: "Divisor to",
  quotientMaxLabel: "Largest quotient",
  dividendDigitsLabel: "Digits in the dividend",
  divisorDigitsLabel: "Digits in the divisor",
  remainderLabel: "Remainders",
  remainders: {
    none: "None — every problem divides evenly",
    any: "Allowed, not required",
    always: "Required — every problem has one",
  },
  layoutNote:
    "Stacked draws the long-division bracket with room to work; one line writes 84 ÷ 7 = ___.",
} as const;

type Mode = "facts" | "digits";

interface Fields {
  divisorMin: number;
  divisorMax: number;
  quotientMax: number;
  dividendDigits: number;
  divisorDigits: number;
  remainder: RemainderMode;
}

const START: Fields = {
  divisorMin: 2,
  divisorMax: 9,
  quotientMax: 10,
  dividendDigits: 3,
  divisorDigits: 1,
  remainder: "none",
};

const PRESETS: Record<
  keyof typeof DIVISION_WORKSHEET_GENERATOR_STRINGS.presets,
  { mode: Mode; fields: Partial<Fields> }
> = {
  tables: {
    mode: "facts",
    fields: {
      divisorMin: 2,
      divisorMax: 5,
      quotientMax: 10,
      remainder: "none",
    },
  },
  tablesAll: {
    mode: "facts",
    fields: {
      divisorMin: 2,
      divisorMax: 12,
      quotientMax: 12,
      remainder: "none",
    },
  },
  short: {
    mode: "digits",
    fields: { dividendDigits: 3, divisorDigits: 1, remainder: "none" },
  },
  shortRem: {
    mode: "digits",
    fields: { dividendDigits: 3, divisorDigits: 1, remainder: "always" },
  },
  long: {
    mode: "digits",
    fields: { dividendDigits: 4, divisorDigits: 2, remainder: "any" },
  },
};

const range = (from: number, to: number) =>
  Array.from({ length: to - from + 1 }, (_, i) => ({
    value: String(from + i),
    label: String(from + i),
  }));

export default function DivisionWorksheetGeneratorWidget() {
  const strings = useToolUi(DIVISION_WORKSHEET_GENERATOR_STRINGS);
  const [settings, update] = useCommonSettings(
    defaultSettings({ count: 30, layout: "horizontal" })
  );
  const seed = useWorksheetSeed();
  const [mode, setMode] = useState<Mode>("facts");
  const [fields, setFields] = useState<Fields>(START);

  const patch = (next: Partial<Fields>) =>
    setFields((current) => ({ ...current, ...next }));
  const options: DivisionOptions =
    mode === "facts"
      ? {
          kind: "facts",
          count: settings.count,
          divisorMin: Math.min(fields.divisorMin, fields.divisorMax),
          divisorMax: Math.max(fields.divisorMin, fields.divisorMax),
          quotientMax: fields.quotientMax,
          remainder: fields.remainder,
        }
      : {
          kind: "digits",
          count: settings.count,
          dividendDigits: fields.dividendDigits,
          divisorDigits: fields.divisorDigits,
          remainder: fields.remainder,
        };
  const generated = generateDivision(options, createRng(seed.seed));

  function switchMode(next: Mode) {
    setMode(next);
    update(
      next === "facts"
        ? { count: 30, layout: "horizontal", columns: 0 }
        : { count: 12, layout: "vertical", columns: 0 }
    );
  }

  function applyPreset(key: keyof typeof PRESETS) {
    const preset = PRESETS[key];
    switchMode(preset.mode);
    setFields({ ...START, ...preset.fields });
  }

  return (
    <div className="space-y-6 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <PillGroup
          label={strings.modeLabel}
          name="dw-mode"
          onChange={switchMode}
          options={(["facts", "digits"] as const).map((value) => ({
            value,
            label: strings.modes[value],
          }))}
          value={mode}
        />
        <div className="space-y-2">
          <p className={LABEL}>{strings.presetsLabel}</p>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(PRESETS) as (keyof typeof PRESETS)[]).map((key) => (
              <button
                className={BUTTON_SMALL}
                key={key}
                onClick={() => applyPreset(key)}
                type="button"
              >
                {strings.presets[key]}
              </button>
            ))}
          </div>
        </div>
        {mode === "facts" ? (
          <div className="grid gap-4 sm:grid-cols-3">
            <SelectField
              id="dw-div-from"
              label={strings.divisorFromLabel}
              onChange={(value) => patch({ divisorMin: Number(value) })}
              options={range(2, 12)}
              value={String(fields.divisorMin)}
            />
            <SelectField
              id="dw-div-to"
              label={strings.divisorToLabel}
              onChange={(value) => patch({ divisorMax: Number(value) })}
              options={range(2, 12)}
              value={String(fields.divisorMax)}
            />
            <SelectField
              id="dw-quotient"
              label={strings.quotientMaxLabel}
              onChange={(value) => patch({ quotientMax: Number(value) })}
              options={range(2, 12)}
              value={String(fields.quotientMax)}
            />
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              id="dw-dividend"
              label={strings.dividendDigitsLabel}
              onChange={(value) => patch({ dividendDigits: Number(value) })}
              options={range(2, 5)}
              value={String(fields.dividendDigits)}
            />
            <SelectField
              id="dw-divisor"
              label={strings.divisorDigitsLabel}
              onChange={(value) => patch({ divisorDigits: Number(value) })}
              options={range(1, 2)}
              value={String(fields.divisorDigits)}
            />
          </div>
        )}
        <SelectField
          hint={strings.layoutNote}
          id="dw-remainder"
          label={strings.remainderLabel}
          onChange={(remainder: RemainderMode) => patch({ remainder })}
          options={(["none", "any", "always"] as const).map((value) => ({
            value,
            label: strings.remainders[value],
          }))}
          value={fields.remainder}
        />
        <CommonControls
          idPrefix="dw"
          onChange={update}
          seed={seed}
          settings={settings}
          strings={strings}
        />
        <UnmetNotice strings={strings} unmet={generated.unmet} />
      </div>
      <ArithmeticSheet
        problems={generated.problems}
        seed={seed.seed}
        settings={settings}
        strings={strings}
        title={settings.title.trim() || strings.defaultTitle}
        withRemainder={fields.remainder !== "none"}
        workRows={mode === "digits" ? Math.max(2, fields.dividendDigits) : 1}
      />
    </div>
  );
}
